// apps/nos/client/src/pages/CreateFlavorPage/steps/ChromatogramFiles.tsx
//
// The chromatogram file-upload surface, split out of ChromatogramMode so the
// high-frequency upload PROGRESS updates stay isolated here. It owns the live
// `chromas` state LOCALLY (seeded once from the parent anchor) and renders live
// progress from it. It syncs back up to the parent (→ anchor → draft store) ONLY
// on COMMITTED changes — a file added/removed or a status/id transition — never
// on a progressPct tick. That keeps per-tick re-renders + localStorage writes out
// of the parent (CreateFlavorPage) and off the sibling "Name this substance"
// input, which used to jank/drop keystrokes during an upload.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { InstrumentPicker, type PickerProfile } from "@/components/InstrumentPicker";
import { deleteUpload, listDraftUploads, patchUpload, useAuthHeaders } from "@/services/uploads_api";
import monitoring from "@/lib/monitoring";
import { useChromatogramUpload } from "@/hooks/useChromatogramUpload";
import {
  useDefaultUserExpConfig,
  useInstrumentsCatalogs,
  useUserExpConfigs,
} from "@/hooks/useInstruments";
import type { ConfigDTO } from "@/services/instruments_api";
import type { GcColumnFamily, InstrumentCatalogEntry } from "@/utils/types/instrument";
import type { Chroma } from "@/utils/types/upload";

const ACCEPTED_EXTENSIONS = [".raw", ".mzML", ".csv", ".xlsx"];

// A user_exp_config has no vendor field — derive it from the hardware catalog
// (type_instrument_id → instrument_provider). Keep a "thermo" fallback because
// findInstrumentCatalog returns null for configs whose provider isn't a known
// vendor (see useInstruments.rowToInstrumentEntry).
const configVendor = (
  configId: string | null,
  configs: ConfigDTO[],
  findInstrumentCatalog: (id: number | null | undefined) => InstrumentCatalogEntry | null,
): "agilent" | "thermo" => {
  if (!configId) return "thermo";
  const cfg = configs.find((c) => c.id === configId);
  const inst = findInstrumentCatalog(cfg?.type_instrument_id ?? null);
  return inst?.vendor === "Agilent" ? "agilent" : "thermo";
};

export function RequiredStar() {
  return (
    <span
      aria-hidden="true"
      style={{ color: "var(--err, #c33)", marginLeft: 4, fontWeight: 600 }}
    >
      *
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

type ChromatogramFilesProps = {
  draftId: string;
  /** Seed for the local working set (rehydration from the persisted draft).
   *  Read once on mount; subsequent parent updates don't reset local progress. */
  initialChromas: Chroma[];
  /** Called only on COMMITTED changes (add/remove/status-or-id transition), so the
   *  anchor/draft don't churn on every progress tick. */
  onChromasChange: (next: Chroma[]) => void;
  chromaConfigById: Record<string, string>;
  onChromaConfigByIdChange: (next: Record<string, string>) => void;

  // ── Presentational props (all optional; defaults preserve current behavior) ──

  /**
   * Hide the "Chromatograms · N files" header row above the upload area.
   * @default false
   */
  hideHeader?: boolean;
  /**
   * Tighten the per-file configuration row (instrument picker + labels).
   * @default false
   */
  compactConfig?: boolean;
  /**
   * Use a richer drop-zone styling (matches the substance drawer design).
   * @default false
   */
  richDropzone?: boolean;
  /**
   * Hide the "New uploads default to…" configuration hint text below the files.
   * @default false
   */
  hideConfigHint?: boolean;
  /**
   * Override the empty-state prompt shown when no files have been uploaded yet.
   * @default "Upload chromatograms of the target food"
   */
  emptyPrompt?: string;
};

export function ChromatogramFiles({
  draftId,
  initialChromas,
  onChromasChange,
  chromaConfigById,
  onChromaConfigByIdChange,
  hideHeader = false,
  compactConfig = false,
  richDropzone = false,
  hideConfigHint = false,
  emptyPrompt = "Upload chromatograms of the target food",
}: ChromatogramFilesProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Live working set — local source of truth (incl. in-flight progress).
  const [chromas, setChromas] = useState<Chroma[]>(() => initialChromas);
  const chromasRef = useRef<Chroma[]>(initialChromas);

  // Real experiment configurations + hardware catalog.
  const { data: configs = [] } = useUserExpConfigs();
  const { data: defaultRow } = useDefaultUserExpConfig();
  const { findInstrumentCatalog, findColumnFamily } = useInstrumentsCatalogs();
  const defaultConfigId = defaultRow?.user_exp_config_id ?? configs[0]?.id ?? null;

  const pickerProfiles = useMemo<PickerProfile[]>(
    () =>
      configs.map((c) => ({
        id: c.id,
        alias: c.name,
        instrumentId: c.type_instrument_id,
        column: { id: c.type_column_id },
      })),
    [configs],
  );
  const defaultConfig = configs.find((c) => c.id === defaultConfigId) ?? null;

  const { start, cancel } = useChromatogramUpload();
  const getHeaders = useAuthHeaders();
  const [dragOver, setDragOver] = useState(false);

  const chromaConfigByIdRef = useRef(chromaConfigById);
  useEffect(() => {
    chromaConfigByIdRef.current = chromaConfigById;
  }, [chromaConfigById]);

  // Seed the default config for any uploaded file without one yet (so submit can
  // forward a config for every file). Converges; waits for the default to load.
  useEffect(() => {
    if (!defaultConfigId) return;
    const missing = chromas.filter(
      (c) =>
        c.status === "uploaded" &&
        !c.fileUploadId.startsWith("tmp-") &&
        !(c.fileUploadId in chromaConfigById),
    );
    if (missing.length === 0) return;
    const next = { ...chromaConfigById };
    for (const c of missing) next[c.fileUploadId] = defaultConfigId;
    onChromaConfigByIdChange(next);
  }, [chromas, chromaConfigById, defaultConfigId, onChromaConfigByIdChange]);

  // Commit `updated` to local state, and bubble to the parent only when asked
  // (committed change). Progress-only ticks update local state silently.
  const apply = useCallback(
    (updated: Chroma[], committed: boolean) => {
      chromasRef.current = updated;
      setChromas(updated);
      if (committed) onChromasChange(updated);
    },
    [onChromasChange],
  );

  // ── Reconcile the local working set against server truth on draft rehydrate ──
  // localStorage chromas can drift from the DB (a file deleted in another tab, a
  // row abandoned, or a dropped delete that finalize later swept). On mount we
  // fetch the draft's server-confirmed 'uploaded' rows and PRUNE any settled
  // local entry the server no longer has, so a stale row can't linger in the
  // picker and surprise finalize. We deliberately do NOT add server rows missing
  // locally: a file the user removed is still 'uploaded' server-side until
  // finalize reconciles it, and resurfacing it would undo the removal. In-flight
  // rows (tmp- / not yet uploaded) are left untouched.
  const reconciledDraftRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draftId || reconciledDraftRef.current === draftId) return;
    // Snapshot the settled rows that exist NOW (at mount) — these are the only
    // candidates for pruning. A brand-new draft (or one with only in-flight
    // uploads) has nothing to reconcile. Capturing the set before the await also
    // means a file that finishes uploading DURING the fetch window is never
    // pruned (it isn't in this snapshot), avoiding a race that would drop a valid
    // just-completed upload.
    const settledAtMount = new Set(
      chromasRef.current
        .filter((c) => c.status === "uploaded" && !c.fileUploadId.startsWith("tmp-"))
        .map((c) => c.fileUploadId),
    );
    if (settledAtMount.size === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const headers = await getHeaders();
        const serverUploads = await listDraftUploads(draftId, headers);
        if (cancelled) return;
        // Mark reconciled only after a SUCCESSFUL response, so a failed fetch
        // (network / transient auth) retries on the next mount instead of
        // silently disabling reconcile for the rest of the session.
        reconciledDraftRef.current = draftId;
        const serverIds = new Set(serverUploads.map((u) => u.file_upload_id));
        const cur = chromasRef.current;
        // Prune ONLY rows present at mount that the server no longer has. Rows
        // added/completed since (not in settledAtMount) are always kept.
        const next = cur.filter((c) =>
          settledAtMount.has(c.fileUploadId) ? serverIds.has(c.fileUploadId) : true,
        );
        if (next.length === cur.length) return; // nothing pruned
        apply(next, true);
        // Drop config-map entries for pruned ids.
        const presentIds = new Set(next.map((c) => c.fileUploadId));
        const curCfg = chromaConfigByIdRef.current;
        const nextCfg: Record<string, string> = {};
        for (const [id, cfg] of Object.entries(curCfg)) {
          if (presentIds.has(id)) nextCfg[id] = cfg;
        }
        if (Object.keys(nextCfg).length !== Object.keys(curCfg).length) {
          onChromaConfigByIdChange(nextCfg);
        }
      } catch {
        // Best-effort; leave reconciledDraftRef unset so a remount retries.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Reconcile once per successfully-reconciled draftId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const handleTransition = useCallback(
    (next: Chroma) => {
      const cur = chromasRef.current;
      // Match on temp id OR real file_upload_id (start() may swap mid-flight).
      const idx = cur.findIndex(
        (c) =>
          c.fileUploadId === next.fileUploadId ||
          (c.fileUploadId.startsWith("tmp-") &&
            c.filename === next.filename &&
            c.sizeBytes === next.sizeBytes),
      );
      if (idx === -1) {
        apply([...cur, next], true); // new file → committed
        return;
      }
      const prev = cur[idx]!;
      // Only a status or id transition is a committed change worth bubbling up;
      // a pure progressPct tick stays local (no parent re-render / draft write).
      const committed = prev.status !== next.status || prev.fileUploadId !== next.fileUploadId;
      const copy = cur.slice();
      copy[idx] = next;
      apply(copy, committed);
    },
    [apply],
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const machine = configVendor(defaultConfigId, configs, findInstrumentCatalog);
      for (const file of Array.from(files)) {
        const lower = file.name.toLowerCase();
        if (!ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext.toLowerCase()))) {
          continue;
        }
        void start({ draftId, file, machine, onTransition: handleTransition });
      }
    },
    [draftId, defaultConfigId, configs, findInstrumentCatalog, start, handleTransition],
  );

  const removeChroma = useCallback(
    async (chroma: Chroma) => {
      const isInFlight =
        chroma.status === "presigning" ||
        chroma.status === "uploading" ||
        chroma.status === "verifying";

      apply(
        chromasRef.current.filter((c) => c.fileUploadId !== chroma.fileUploadId),
        true, // removal → committed
      );

      // Drop the per-file config entry so the map never carries a removed id.
      const curCfg = chromaConfigByIdRef.current;
      if (chroma.fileUploadId in curCfg) {
        const nextCfg = { ...curCfg };
        delete nextCfg[chroma.fileUploadId];
        onChromaConfigByIdChange(nextCfg);
      }

      if (isInFlight) {
        // The upload hook owns server cleanup for in-flight rows (it knows the
        // real id + phase and deletes the row it created on abort). Just cancel.
        cancel(chroma.fileUploadId);
        return;
      }
      if (chroma.fileUploadId.startsWith("tmp-")) return;

      // Completed upload — reliable, retrying delete. Surface a hard failure to
      // monitoring; finalize still reconciles any straggler server-side.
      try {
        const headers = await getHeaders();
        await deleteUpload(chroma.fileUploadId, headers);
      } catch (err) {
        monitoring.error(
          "chromatogram_delete_failed",
          err instanceof Error ? err : new Error(String(err)),
          { extra: { fileUploadId: chroma.fileUploadId } },
        );
      }
    },
    [apply, getHeaders, cancel, onChromaConfigByIdChange],
  );

  const setConfigForUpload = useCallback(
    async (chroma: Chroma, configId: string) => {
      onChromaConfigByIdChange({
        ...chromaConfigByIdRef.current,
        [chroma.fileUploadId]: configId,
      });
      const machine = configVendor(configId, configs, findInstrumentCatalog);
      apply(
        chromasRef.current.map((c) =>
          c.fileUploadId === chroma.fileUploadId ? { ...c, machine } : c,
        ),
        true, // machine change → committed
      );
      if (chroma.fileUploadId.startsWith("tmp-")) return;
      try {
        const headers = await getHeaders();
        await patchUpload(chroma.fileUploadId, { metadata: { machine } }, headers);
      } catch {
        // Leave optimistic state; server reconciles on next finalize.
      }
    },
    [apply, getHeaders, onChromaConfigByIdChange, configs, findInstrumentCatalog],
  );

  const onDrop: React.DragEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onPickFiles = () => fileInputRef.current?.click();

  return (
    <div className="vstack gap-2">
      {!hideHeader && (
        <div className="hstack" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, fontWeight: 500 }}>
            Chromatograms
            <RequiredStar />
            {chromas.length > 0 && (
              <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)", marginLeft: 8 }}>
                {chromas.length} file{chromas.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            .raw · .mzML · .csv · .xlsx
          </span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {chromas.length === 0 ? (
        <button
          type="button"
          onClick={onPickFiles}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn("chroma-upload-empty", !richDropzone && dragOver && "drag-over")}
          style={richDropzone ? {
            padding: "18px 16px",
            borderRadius: 8,
            border: `1.5px dashed ${dragOver ? "var(--accent-ink)" : "var(--line-strong)"}`,
            background: dragOver ? "var(--accent-soft)" : "var(--bg-sunk)",
            width: "100%",
            textAlign: "center",
            cursor: "pointer",
          } : undefined}
        >
          <div style={{ marginBottom: 8 }}>
            <Icon name="upload" size={22} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)" }}>
            {emptyPrompt}
          </div>
          <div className="mono" style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>
            Drop multiple files here, or click to browse · up to 3 GB each
          </div>
        </button>
      ) : (
        <div
          className="vstack gap-2"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {chromas.map((c) => (
            <ChromaRow
              key={c.fileUploadId}
              chroma={c}
              configId={chromaConfigById[c.fileUploadId] ?? defaultConfigId}
              profiles={pickerProfiles}
              findInstrumentCatalog={findInstrumentCatalog}
              findColumnFamily={findColumnFamily}
              onRemove={() => void removeChroma(c)}
              onSetConfig={(id) => void setConfigForUpload(c, id)}
              onOpenInstruments={() => navigate("/instruments")}
              compact={compactConfig}
            />
          ))}
          <button
            type="button"
            onClick={onPickFiles}
            className="btn xs ghost"
            style={{ alignSelf: "flex-start", padding: "6px 10px", fontSize: 12 }}
          >
            <Icon name="plus" size={11} />
            Add another chromatogram
          </button>
        </div>
      )}

      {/* Default-configuration hint */}
      {!hideConfigHint && <div
        className="hstack"
        style={{
          gap: 6,
          fontSize: 11,
          color: "var(--ink-4)",
          lineHeight: 1.5,
          alignItems: "flex-start",
          marginTop: 2,
        }}
      >
        <span style={{ marginTop: 1 }}>
          <Icon name="info" size={11} />
        </span>
        <span>
          New uploads default to the experiment configuration{" "}
          <span style={{ fontWeight: 500, color: "var(--ink-3)" }}>
            {defaultConfig ? defaultConfig.name : "your saved default"}
          </span>{" "}
          — switch any file's configuration so we parse it against the right method, or{" "}
          <button
            type="button"
            onClick={() => navigate("/instruments")}
            style={{
              color: "var(--accent-ink)",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              padding: 0,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontSize: "inherit",
            }}
          >
            manage chromatography configurations
          </button>
          .
        </span>
      </div>}
    </div>
  );
}

function ChromaRow({
  chroma,
  configId,
  profiles,
  findInstrumentCatalog,
  findColumnFamily,
  onRemove,
  onSetConfig,
  onOpenInstruments,
  compact = false,
}: {
  chroma: Chroma;
  configId: string | null;
  profiles: PickerProfile[];
  findInstrumentCatalog: (id: number | null | undefined) => InstrumentCatalogEntry | null;
  findColumnFamily: (id: number | null | undefined) => GcColumnFamily | null;
  onRemove: () => void;
  onSetConfig: (id: string) => void;
  onOpenInstruments: () => void;
  /** Tighten the config label + picker row. @default false */
  compact?: boolean;
}) {
  const sizeText = formatBytes(chroma.sizeBytes);
  const inFlight =
    chroma.status === "presigning" ||
    chroma.status === "uploading" ||
    chroma.status === "verifying";
  return (
    <div className="chroma-row">
      <div className="chroma-row-icon">
        <Icon name="file" size={14} />
      </div>
      <div className="vstack" style={{ flex: 1, lineHeight: 1.3, minWidth: 0, gap: 2 }}>
        <div className="hstack" style={{ gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {chroma.filename}
          </span>
          <span className={cn("chroma-format-tag", chroma.format)}>.{chroma.format}</span>
        </div>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          <ChromaStatusText chroma={chroma} sizeText={sizeText} />
        </span>
      </div>

      <div className="vstack" style={{ gap: compact ? 1 : 3, flexShrink: 0 }}>
        {!compact && (
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--ink-4)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Experiment config
          </span>
        )}
        <InstrumentPicker
          value={configId}
          onChange={onSetConfig}
          profiles={profiles}
          findInstrumentCatalog={findInstrumentCatalog}
          findColumnFamily={findColumnFamily}
          onOpenManage={onOpenInstruments}
          align="right"
        />
      </div>

      <button
        type="button"
        className="btn xs ghost"
        onClick={onRemove}
        title={inFlight ? "Cancel upload" : "Remove"}
        style={{ flexShrink: 0 }}
      >
        <Icon name={inFlight ? "x" : "trash"} size={11} />
      </button>
    </div>
  );
}

function ChromaStatusText({ chroma, sizeText }: { chroma: Chroma; sizeText: string }) {
  switch (chroma.status) {
    case "presigning":
      return <>Preparing upload · {sizeText}</>;
    case "uploading":
      return (
        <>
          Uploading {chroma.progressPct ?? 0}% · {sizeText}
        </>
      );
    case "verifying":
      return <>Verifying · {sizeText}</>;
    case "uploaded":
      return <>Uploaded · {sizeText}</>;
    case "failed":
      return (
        <span style={{ color: "var(--err, #c33)" }}>
          Failed: {chroma.errorMessage ?? "unknown error"}
        </span>
      );
  }
}
